import { DateRange } from "react-day-picker";

import type { FilterValue } from "../config/types";

export const isStructuredSearchApplyShortcut = ({
  ctrlKey,
  key,
  metaKey,
}: {
  ctrlKey: boolean;
  key: string;
  metaKey: boolean;
}): boolean => key === "Enter" && (metaKey || ctrlKey);

export const serializeDateTimeValue = (date: Date): string => date.toISOString();

const padToTwoDigits = (value: number): string => String(value).padStart(2, "0");

export const formatDateTimeForDisplay = (date: Date): string => {
  const month = padToTwoDigits(date.getMonth() + 1);
  const day = padToTwoDigits(date.getDate());
  const year = padToTwoDigits(date.getFullYear() % 100);
  const rawHours = date.getHours();
  const meridiem = rawHours >= 12 ? "pm" : "am";
  const hours12 = rawHours % 12 || 12;
  const hours = padToTwoDigits(hours12);
  const minutes = padToTwoDigits(date.getMinutes());
  const seconds = padToTwoDigits(date.getSeconds());
  return `${month}-${day}-${year} ${hours}:${minutes}:${seconds} ${meridiem}`;
};

export const parseDateFromValue = (value: string): Date | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed;
};

export const parseDateRangeValue = (value: FilterValue | undefined): DateRange | undefined => {
  if (!value) {
    return undefined;
  }

  if (typeof value === "string") {
    const parsedSingle = parseDateFromValue(value);
    if (parsedSingle) {
      return { from: parsedSingle, to: undefined };
    }
  }

  let normalizedRange: unknown;
  if (Array.isArray(value)) {
    normalizedRange = value;
  } else if (typeof value === "string") {
    try {
      normalizedRange = JSON.parse(value);
    } catch {
      normalizedRange = undefined;
    }
  }

  if (!Array.isArray(normalizedRange) || normalizedRange.length < 2) {
    return undefined;
  }

  const from = parseDateFromValue(String(normalizedRange[0] ?? ""));
  const to = parseDateFromValue(String(normalizedRange[1] ?? ""));
  if (!from) {
    return undefined;
  }
  if (!to) {
    return { from, to: undefined };
  }
  return { from, to };
};

export const serializeDateRangeValue = (date: DateRange): string =>
  JSON.stringify([serializeDateTimeValue(date.from!), serializeDateTimeValue(date.to!)]);

export const isCompletedDateRange = (date: DateRange | undefined): date is Required<DateRange> =>
  !!date?.from && !!date?.to;

interface InputPlaceholderOptions {
  isMultiSelect: boolean;
  multiCount: number;
  step: string;
  fallback: string;
  operator?: string;
}

export const getInputPlaceholder = ({
  isMultiSelect,
  multiCount,
  step,
  fallback,
  operator,
}: InputPlaceholderOptions): string => {
  if (step === "operator") {
    return "Choose operator";
  }

  if (isMultiSelect) {
    return multiCount > 0
      ? "Add another value or comma-separated list, then press Enter"
      : "Enter a value or comma-separated list, then press Enter";
  }

  if (step === "value") {
    if (operator === "key_contains" || operator === "key_not_contains") {
      return "Search within value...";
    }
    return "Enter exact value";
  }

  return fallback;
};

/** Format cost for display, ensuring $ prefix. */
export const formatCostForDisplay = (value: number | string): string => {
  if (typeof value === "number") {
    return `$${value.toFixed(6)}`;
  }
  const trimmed = String(value).trim();
  return trimmed.startsWith("$") ? trimmed : `$${trimmed}`;
};
